import { HelpCircle, HelpCircleIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePageHelp } from "@/hooks/use-page-help";
import { cn } from "@/lib/utils";

export function HelpToggle({ className }: { className?: string }) {
  const { helpEnabled, toggleHelp, helpItems } = usePageHelp();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8 transition-colors",
              helpEnabled
                ? "text-amber-600 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40"
                : "text-muted-foreground hover:text-foreground",
              className
            )}
            onClick={toggleHelp}
            data-testid="button-help-toggle"
            aria-label={helpEnabled ? "Hide help labels" : "Show help labels"}
          >
            <HelpCircleIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {helpEnabled
            ? `Hide help icons${helpItems.length > 0 ? ` (${helpItems.length} items)` : ""}`
            : `Show help icons${helpItems.length > 0 ? ` (${helpItems.length} items)` : ""}`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function HelpIcon({ elementKey, className }: { elementKey: string; className?: string }) {
  const { helpEnabled, getHelpItem } = usePageHelp();
  const item = getHelpItem(elementKey);

  if (!helpEnabled || !item) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-center rounded-full text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400",
              className
            )}
            data-testid={`help-icon-${elementKey}`}
            aria-label={`Help: ${item.title}`}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-sm">
          <p className="font-medium mb-1">{item.title}</p>
          <p className="text-muted-foreground">{item.tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function HelpLabel({ elementKey, className }: { elementKey: string; className?: string }) {
  const { helpEnabled, getHelpItem } = usePageHelp();
  const item = getHelpItem(elementKey);

  if (!helpEnabled || !item) return null;

  return (
    <p
      className={cn(
        "text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1 mt-1",
        className
      )}
      data-testid={`help-label-${elementKey}`}
    >
      {item.tooltipText}
    </p>
  );
}
